import { createContext, ReactNode, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { UserCoursesDTO } from '../../../apis/api.signup'
import { ROLES } from '../../../constants'
import { useUser } from '../../../context/UserContext'
import { useCourses } from '../../../hooks/ApiHooks/useCourses'
import { MenuProps } from '../../../layouts/NavBar/useNavBarItems'

const Context = createContext({} as NetworkContext)
Context.displayName = 'NetworkContext'

export const useNetwork = () => useContext<NetworkContext>(Context)

export const NetworkContextProvider = ({ networkId, children }: { networkId: string; children?: ReactNode }) => {
    const { t } = useTranslation('teamManager')
    const { role } = useUser()
    const { getNetworkData, getCoursesByNetwork } = useCourses()

    // Get network (course) data
    const networkData = useMemo(() => getNetworkData(networkId), [networkId, getNetworkData])

    // Get all cohorts for this network
    const cohorts = useMemo(() => getCoursesByNetwork(networkId), [networkId, getCoursesByNetwork])

    // Build navigation links for network level
    const mainLinks: MenuProps[] = [
        {
            name: t('main-links.cohorts', { defaultValue: 'Cohortes' }),
            url: `/network/${networkId}/cohorts`,
            active: window.location.pathname.includes('/cohorts') && !window.location.pathname.includes('/cohorts/'),
            icon: ['far', 'list'],
            activeIcon: ['far', 'list'],
        },
    ]

    // Add unified reports menu only for COACH role
    if (role !== ROLES.PROFESSOR) {
        mainLinks.push({
            name: t('main-links.unified-reports', { defaultValue: 'Reportes del Curso' }),
            url: `/network/${networkId}/reports`,
            active: window.location.pathname.includes(`/network/${networkId}/reports`),
            icon: ['far', 'chart-mixed'],
            activeIcon: ['far', 'chart-mixed'],
        })
    }

    useEffect(() => {
        sessionStorage.setItem('last-networkId', networkId)
    }, [networkId])

    return (
        <Context.Provider
            value={{
                networkId,
                networkName: networkData?.networkName || '',
                courseName: networkData?.networkName || '',
                cohorts,
                mainLinks,
            }}
        >
            {children}
        </Context.Provider>
    )
}

// Interface
interface NetworkContext {
    networkId: string
    networkName: string
    courseName: string
    cohorts: UserCoursesDTO[]
    mainLinks: MenuProps[]
}
